import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Button, FormControl, FormLabel, Grid, Input, MenuItem, Select } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import OrgApiService from '../../../api/OrgApiService';
import { compareObjects } from '../../Utilities';
import LocationApiService from '../../../api/LocationApiService';
import { entityAlreadyExists, ok } from '../../../constants/apiStatusConstants';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AuthUtil from '../../Utils/AuthUtil';
import SaveButton from '../../Common/SaveButton';
import { useAsyncCallback } from '../../../hooks/useAsyncCallback';
import { useSnackbar } from '../../Common/SnackbarProvider';

const orgApiService = new OrgApiService();
const locationApiService = new LocationApiService();

export default function EditLocationModal (props) {
    const {
        open,
        currentLocation,
        onCancel,
        onSave,
    } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const [locationName, setLocationName] = useState('');
    const [active, setActive] = useState(0);
    const [projectDefault, setProjectDefault] = useState(0);
    const [description, setDescription] = useState('');
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState(undefined);
    const [title, setTitle] = useState(t('editLocation.title.create'));

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const isSuperAdmin = useMemo(() => AuthUtil.isSuperAdmin(userToken), [userToken]);

    const populateOrganizations = async () => {
        let orgResponse = await orgApiService.getOrgs();

        if (orgResponse != null)
        {
            setOrganizations(orgResponse.data);
            if (currentLocation)
            {
                setSelectedOrganization(orgResponse.data.find(org => org.ogId === currentLocation.pjOrg));
            }
        }
    }

    useEffect(() => {
        if (open) {
            if (currentLocation)
            {
                setTitle(t('editLocation.title.edit', { name: currentLocation.pjName }));
            }
            else
            {
                setTitle(t('editLocation.title.create'));
            }

            setLocationName(currentLocation?.pjName || '');
            setActive(currentLocation?.pjActive || 0);
            setProjectDefault(currentLocation?.pjDefault || 0);
            setDescription(currentLocation?.pjDescription || '');
            setSelectedOrganization(undefined);

            if (isSuperAdmin)
            {
                void populateOrganizations();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, currentLocation]);

    const { run: handleSave, loading: saving } = useAsyncCallback(async () => {
        let newLocation = getUpdatedLocation();
        let locationResponse;

        if (!locationName)
        {
            snackbar.error(t('editLocation.validation.nameRequired'));
            return;
        }

        if (!currentLocation) {
            locationResponse = await locationApiService.addLocation(newLocation);

            if (locationResponse.status === ok)
            {
                snackbar.success(t('editLocation.toast.created', { name: newLocation.pjName }));
                onSave();
                return;
            }
        }
        else if (!compareObjects(newLocation, currentLocation)) {
            newLocation.pjId = currentLocation.pjId;

            locationResponse = await locationApiService.updateLocation(newLocation);

            if (locationResponse.status === ok)
            {
                snackbar.success(t('editLocation.toast.updated', { name: newLocation.pjName }));
                onSave();
                return;
            }
        }
        else
        {
            onSave();
            return;
        }

        if (locationResponse.status === entityAlreadyExists)
        {
            snackbar.error(t('editLocation.toast.alreadyExists'));
        }
        else
        {
            snackbar.error(t('editLocation.toast.saveFailed'));
        }
    });

    function getUpdatedLocation () {
        return {
            pjName: locationName,
            pjActive: active,
            pjDefault: projectDefault,
            pjDescription: description,
            pjOrg: selectedOrganization?.ogId,
        };
    }

    return (
        <Dialog
            sx={{ '& .MuiDialog-paper': { width: '175%' } }}
            maxWidth="lg"
            fullWidth
            open={open}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                <Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <FormLabel htmlFor="orgName">{t('editLocation.field.name')}</FormLabel>
                                <Input
                                    type="text"
                                    value={locationName}
                                    onChange={(event) => {
                                        setLocationName(event.target.value);
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox checked={active === 1}
                                                   onChange={() => setActive(active === 0 ? 1 : 0)}
                                />}
                                label={t('common.active')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox checked={projectDefault === 1}
                                                   onChange={() => setProjectDefault(projectDefault === 0 ? 1 : 0)}
                                />}
                                label={t('editLocation.field.default')}
                            />
                        </Grid>
                        {isSuperAdmin &&
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <FormLabel htmlFor="organization">{t('fieldName.organization')}</FormLabel>
                                    <Select
                                        name="organization"
                                        id="organization"
                                        value={selectedOrganization ? selectedOrganization.ogName : ''}
                                        onChange={(event) => {
                                            setSelectedOrganization(organizations.find(org => org.ogName === event.target.value));
                                        }}
                                    >
                                        {organizations.map((org) => (<MenuItem key={org.ogId} value={org.ogName}>
                                            {org.ogName}
                                        </MenuItem>))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        }
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <FormLabel htmlFor="orgName">{t('fieldName.description')}</FormLabel>
                                <textarea
                                    value={description}
                                    onChange={(event) => {
                                        setDescription(event.target.value);
                                    }}
                                    rows={4}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button autoFocus variant="contained" onClick={onCancel} disabled={saving}>
                    {t('common.cancel')}
                </Button>
                <SaveButton loading={saving} variant="contained" onClick={handleSave}>{t('common.save')}</SaveButton>
            </DialogActions>
        </Dialog>
    );
}
